/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * Models the SDPrimaryRIDPAnswerStruct
 *
 * @class SDPrimaryRIDPAnswerStruct
 */
class SDPrimaryRIDPAnswerStruct {
  /**
   * Creates an instance of SDPrimaryRIDPAnswerStruct.
   * @param {*} json
   * @memberof SDPrimaryRIDPAnswerStruct
   */
  constructor(json = null) {
    if (json) {
      // string
      this.answerOption = json.answerOption;

      // integer
      this.answerValue = json.answerValue;
    }
  }
}

/**
 * Models the SDPrimaryRIDPQuestionStruct
 *
 * @class SDPrimaryRIDPQuestionStruct
 */
class SDPrimaryRIDPQuestionStruct {
  /**
   * Creates an instance of SDPrimaryRIDPQuestionStruct.
   * @param {*} json
   * @memberof SDPrimaryRIDPQuestionStruct
   */
  constructor(json = null) {
    if (json) {
      // string
      this.question = json.question;

      // integer
      this.questionValue = json.questionValue;

      // array - SDPrimaryRIDPAnswerStruct
      if (json.dtls) {
        this.dtls = json.dtls.map(object => new SDPrimaryRIDPAnswerStruct(object));
      } else if (json.constructor === Array) {
        this.dtls = json.map(object => new SDPrimaryRIDPAnswerStruct(object));
      }
    }
  }
}

/**
 * Models the SDPrimaryRIDPResponseStruct
 *
 * @class SDPrimaryRIDPResponseStruct
 */
class SDPrimaryRIDPResponseStruct {
  /**
   * Creates an instance of SDPrimaryRIDPResponseStruct.
   * @param {*} json
   * @memberof SDPrimaryRIDPResponseStruct
   */
  constructor(json = null) {
    if (json) {
      // string
      this.dshReferenceNumber = json.dshReferenceNumber;

      // string
      this.sessionIdentifier = json.sessionIdentifier;

      // string
      this.responseCode = json.responseCode;

      // string
      this.ridpServiceStatus = json.ridpServiceStatus;

      // array - SDPrimaryRIDPQuestionStruct
      if (json.questionDtlsList) {
        this.questionDtlsList = json.questionDtlsList.map(
          object => new SDPrimaryRIDPQuestionStruct(object)
        );
      } else if (json.constructor === Array) {
        this.questionDtlsList = json.map(object => new SDPrimaryRIDPQuestionStruct(object));
      }
    }
  }
}

/**
 * Models the NewSDRIDPAnswers
 *
 * @class NewSDRIDPAnswers
 */
class NewSDRIDPAnswers {
  /**
   * Creates an instance of NewSDRIDPAnswers.
   * @param {*} json
   * @memberof NewSDRIDPAnswers
   */
  constructor(json = null) {
    if (json) {
      // integer
      this.answerValue = json.answerValue;

      // integer
      this.questionValue = json.questionValue;
    }
  }
}

/**
 * Models the NewSDSecondaryRIDPRequestStruct
 *
 * @class NewSDSecondaryRIDPRequestStruct
 */
class NewSDSecondaryRIDPRequestStruct {
  /**
   * Creates an instance of NewSDSecondaryRIDPRequestStruct.
   * @param {*} json
   * @memberof NewSDSecondaryRIDPRequestStruct
   */
  constructor(json = null) {
    if (json) {
      // string
      this.sessionIdentifier = json.sessionIdentifier;

      // array - NewSDRIDPAnswers
      if (json.dtls) {
        this.dtls = json.dtls.map(object => new NewSDRIDPAnswers(object));
      } else if (json.constructor === Array) {
        this.dtls = json.map(object => new NewSDRIDPAnswers(object));
      }
    }
  }
}

/**
 * Models the NewSDSecondaryRIDPResponseStructResponse
 *
 * @class NewSDSecondaryRIDPResponseStructResponse
 */
class NewSDSecondaryRIDPResponseStructResponse {
  /**
   * Creates an instance of NewSDSecondaryRIDPResponseStructResponse.
   * @param {*} json
   * @memberof NewSDSecondaryRIDPResponseStructResponse
   */
  constructor(json = null) {
    if (json) {
      // string
      this.dshReferenceNumber = json.dshReferenceNumber;

      // string
      this.ridpServiceStatus = json.ridpServiceStatus;

      // string
      this.responseCode = json.responseCode;

      // string
      this.sessionIdentifier = json.sessionIdentifier;
    }
  }
}

/**
 * Models the SDFARSResponseStruct
 *
 * @class SDFARSResponseStruct
 */
class SDFARSResponseStruct {
  /**
   * Creates an instance of SDFARSResponseStruct.
   * @param {*} json
   * @memberof SDFARSResponseStruct
   */
  constructor(json = null) {
    if (json) {
      // string
      this.ridpServiceStatus = json.ridpServiceStatus;

      // string
      this.responseCode = json.responseCode;
    }
  }
}

/**
 * Models the SDRIDPRequest
 *
 * @class SDRIDPRequest
 */
class SDRIDPRequest {
  /**
   * Creates an instance of SDRIDPRequest.
   * @param {*} json
   * @memberof SDRIDPRequest
   */
  constructor(json = null) {
    if (json) {
      // string
      this.hdimPayloadText = json.hdimPayloadText;

      // string
      this.serverDateTime = json.serverDateTime;

      // string
      this.hostText = json.hostText;

      // string
      this.originText = json.originText;

      // string
      this.userAgentText = json.userAgentText;

      // string
      this.cacheControlText = json.cacheControlText;

      // string
      this.contentLength = json.contentLength;

      // string
      this.referrerText = json.referrerText;

      // string
      this.ipAddress = json.ipAddress;

      // string
      this.jscText = json.jscText;

      // string
      this.deviceLanguage = json.deviceLanguage;

      // string
      this.deviceContentType = json.deviceContentType;

      // string
      this.deviceContentEncoding = json.deviceContentEncoding;
    }
  }
}

/**
 * Models the SDRIDPResponse
 *
 * @class SDRIDPResponse
 */
class SDRIDPResponse {
  /**
   * Creates an instance of SDRIDPResponse.
   * @param {*} json
   * @memberof SDRIDPResponse
   */
  constructor(json = null) {
    if (json) {
      // string
      this.hubReferenceNumber = json.hubReferenceNumber;

      // string
      this.ridpServiceStatus = json.ridpServiceStatus;

      // string
      this.responseCode = json.responseCode;

      // string
      this.sessionIdentifier = json.sessionIdentifier;
    }
  }
}

/**
 * Models the SDGetRecordRequest
 *
 * @class SDGetRecordRequest
 */
class SDGetRecordRequest {
  /**
   * Creates an instance of SDGetRecordRequest.
   * @param {*} json
   * @memberof SDGetRecordRequest
   */
  constructor(json = null) {
    if (json) {
      // string
      this.dshReferenceNumber = json.dshReferenceNumber;
    }
  }
}

/**
 * Models the SDGetRecordResponse
 *
 * @class SDGetRecordResponse
 */
class SDGetRecordResponse {
  /**
   * Creates an instance of SDGetRecordResponse.
   * @param {*} json
   * @memberof SDGetRecordResponse
   */
  constructor(json = null) {
    if (json) {
      // string
      this.ridpServiceStatus = json.ridpServiceStatus;

      // string
      this.responseCode = json.responseCode;
    }
  }
}

export default {
  SDPrimaryRIDPAnswerStruct,

  SDPrimaryRIDPQuestionStruct,

  SDPrimaryRIDPResponseStruct,

  NewSDRIDPAnswers,

  NewSDSecondaryRIDPRequestStruct,

  NewSDSecondaryRIDPResponseStructResponse,

  SDFARSResponseStruct,

  SDRIDPRequest,

  SDRIDPResponse,

  SDGetRecordRequest,

  SDGetRecordResponse,
};
